/**
 * What the Prospects page shows instead of a list: prospecting not available (off for this advisor,
 * or no consent yet to read their whole book — which is given on the CRM's field-map page),
 * prospecting paused for the only CRM they have, a temporary outage, nothing synced from the CRM yet,
 * a book not scored yet, a view with nobody in it, or a page past the end of a list that got shorter.
 *
 * `crmName` is the CRM the card is about. Without one the wording stays general, since the page may
 * not know yet which CRM the advisor will connect.
 *
 * A book that is on its way to being scored is shown as on its way: the steps it goes through, which
 * one it is on, and a spinner. It used to be a plain empty card, which read as "nothing here".
 */
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle, Spinner, Steps } from '@design-system';
import type { ProspectsUnavailable } from '../hooks/use-prospects';

type StepStatus = 'completed' | 'current' | 'upcoming';

function scoringSteps(crm: string, syncing: boolean): { id: string; label: string; status: StepStatus }[] {
  return [
    { id: 'connect', label: `Connect ${crm}`, status: 'completed' },
    { id: 'sync', label: 'Sync your contacts', status: syncing ? 'current' : 'completed' },
    { id: 'score', label: 'Score your contacts', status: syncing ? 'upcoming' : 'current' },
  ];
}

interface ProspectsStatusProps {
  kind: ProspectsUnavailable | 'paused' | 'notScoredYet' | 'waitingForFirstSync' | 'emptyView' | 'pastLastPage';
  crmName?: string;
  message?: string | null;
  onGoToIntegrations?: () => void;
  onRetry?: () => void;
  onClearFilters?: () => void;
  onFirstPage?: () => void;
}

export function ProspectsStatus({ kind, crmName, message, onGoToIntegrations, onRetry, onClearFilters, onFirstPage }: ProspectsStatusProps) {
  const crm = crmName ?? 'your CRM';
  const content = {
    notAvailable: {
      title: 'Prospects are not available yet',
      description:
        message || `Connect ${crm}, map your fields and give consent to read your book on the Integrations page.`,
      action: onGoToIntegrations && <Button onClick={onGoToIntegrations}>Go to Integrations</Button>,
    },
    paused: {
      title: `Prospects are paused for ${crm}`,
      description: `Your ${crm} connection, field mapping and saved contacts are unchanged. Nothing is being read or scored while this is paused.`,
      action: null,
    },
    temporarilyUnavailable: {
      title: 'Prospects are temporarily unavailable',
      description: message || 'We could not confirm your access just now. Try again in a minute.',
      action: onRetry && <Button variant="outline" onClick={onRetry}>Try again</Button>,
    },
    error: {
      title: 'Something went wrong loading your prospects',
      description: message || 'Try again in a minute.',
      action: onRetry && <Button variant="outline" onClick={onRetry}>Try again</Button>,
    },
    notScoredYet: {
      title: 'Scoring your contacts',
      description: `Your ${crm} contacts are synced and are being scored in the background. This usually takes a few minutes, and this page updates on its own.`,
      action: null,
    },
    waitingForFirstSync: {
      title: `Waiting for your first sync from ${crm}`,
      description: `Your contacts are scored once all of them have been read from ${crm}. This page updates on its own. If nothing changes, check your field mapping and sync on the Integrations page.`,
      action: onGoToIntegrations && <Button variant="outline" onClick={onGoToIntegrations}>Go to Integrations</Button>,
    },
    pastLastPage: {
      title: 'This page is past the end of the list',
      description: 'The list got shorter since this page was opened.',
      action: onFirstPage && <Button variant="outline" onClick={onFirstPage}>Back to the first page</Button>,
    },
    emptyView: {
      title: 'No contacts match this view',
      description: 'None of your contacts match this view and these filters.',
      action: onClearFilters && <Button variant="outline" onClick={onClearFilters}>Clear filters</Button>,
    },
  }[kind];

  const inProgress = kind === 'notScoredYet' || kind === 'waitingForFirstSync';

  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle className={inProgress ? 'flex items-center justify-center gap-2' : undefined}>
          {inProgress && <Spinner aria-hidden="true" className="text-primary" />}
          {content.title}
        </EmptyTitle>
        <EmptyDescription>{content.description}</EmptyDescription>
      </EmptyHeader>
      {inProgress && (
        <Steps
          aria-label="Steps to your first scores"
          items={scoringSteps(crm, kind === 'waitingForFirstSync')}
          className="w-auto"
        />
      )}
      {content.action && <EmptyContent>{content.action}</EmptyContent>}
    </Empty>
  );
}
